import { HttpClient, HttpParams } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import apiEndpoints from 'src/app/misc/api-endpoints.misc';
import { User, UserPagination } from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  @Output() successEvent = new EventEmitter<any>();
  @Output() failureEvent = new EventEmitter<any>();

  private usersSubject: BehaviorSubject<User[]> = new BehaviorSubject<User[]>([]);
  public users$: Observable<User[]> = this.usersSubject.asObservable();

  constructor(private httpClient: HttpClient) { }

  getUsers(): Observable<User[]> {
    if (this.usersSubject.getValue().length === 0) {
      return this.httpClient.get<User[]>(`${apiEndpoints.usersUrl}`).pipe(
        tap(users => this.usersSubject.next(users))
      );
    } else {
      return this.users$;
    }
  }

  getUser(id: any): Observable<User> {
    return this.httpClient.get<User>(`${apiEndpoints.usersUrl}${id}`);
  }

  getUsersWithPagination(pagination: UserPagination): Observable<any> {
    let params = new HttpParams();

    if (pagination.page !== undefined) {
      params = params.set('page', pagination.page.toString());
    }

    if (pagination.page_size !== undefined) {
      params = params.set('page_size', pagination.page_size.toString());
    }

    if (pagination.is_active !== undefined) {
      params = params.set('is_active', pagination.is_active.toString());
    }

    if (pagination.search) {
      params = params.set('search', pagination.search);
    }

    if (pagination.role) {
      params = params.set('role', pagination.role);
    }

    return this.httpClient.get<any>(`${apiEndpoints.usersUrl}`, { params });
  }

  saveUser(data: any): Observable<User> {
    return this.httpClient.post<User>(`${apiEndpoints.authUrl}users/`, data).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  createUser(data: any): Observable<User> {
    return this.httpClient.post<User>(`${apiEndpoints.authUrl}users/`, data).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  updateUserRole(data: any): Observable<User> {
    return this.httpClient.patch<User>(`${apiEndpoints.usersUrl}assign-role/`, data).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  getUserGroups(userId: number): Observable<any[]> {
    return this.httpClient.get<any[]>(`${apiEndpoints.groupsUrl}user/${userId}/`);
  }

  addUsersToGroup(groupId: number, users: number[]): Observable<any> {
    return this.httpClient.post<any>(`${apiEndpoints.groupsUrl}${groupId}/add-users/`, { users }).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  removeUsersFromGroup(groupId: number, users: number[]): Observable<any> {
    return this.httpClient.post<any>(`${apiEndpoints.groupsUrl}${groupId}/remove-users/`, { users }).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  getUserPermissions(userId: number): Observable<any> {
    return this.httpClient.get<any>(`${apiEndpoints.permissionsUrl}user/${userId}/`);
  }

  assignPermissionsToUser(userId: number, permissions: number[]): Observable<any> {
    return this.httpClient.post<any>(`${apiEndpoints.usersUrl}${userId}/assign-permissions/`, { permissions }).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  removePermissionsFromUser(userId: number, permissions: number[]): Observable<any> {
    return this.httpClient.post<any>(`${apiEndpoints.usersUrl}${userId}/remove-permissions/`, { permissions }).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  updateUser(id: any, data: any): Observable<User> {
    return this.httpClient.patch<User>(`${apiEndpoints.usersUrl}${id}/`, data).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  updateOwnProfile(userId: number, data: any): Observable<User> {
    return this.httpClient.patch<User>(`${apiEndpoints.usersUrl}${userId}/`, data).pipe(
      tap(() => this.clearUsersCache())
    );
  }

  deleteUser(id: string): Observable<User> {
    return this.httpClient.delete<User>(`${apiEndpoints.usersUrl}${id}/`).pipe(
      tap(() => this.clearUsersCache())
    );
  }


  activateUserCompte(id: number, active: boolean): Observable<User> {
    const playload = {
      id: id,
      status: active
     };
    return this.httpClient.post<User>(
      `${apiEndpoints.authUrl}activation/status/`,
      playload
    ).pipe(
      tap(() => this.clearUsersCache())
    );
  }

   resendVerification(data: any) {
    return this.httpClient.post<any>(`${apiEndpoints.resendVerificationUrl}`, data);
  }


  onSuccess(data: any) {
    this.successEvent.emit(data);
  }

  onFailure(data: any) {
    this.failureEvent.emit(data);
  }

  private clearUsersCache() {
    this.usersSubject.next([]);
  }
}
